/**
 * PRP workspace on phones: a compact top bar, a bottom tab bar for the
 * screens a counselor uses in the field, and a slide-out menu with the full
 * PRP navigation. Replaces the desktop sidebar below the `md` breakpoint.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ListChecks, Menu, Plus, Users } from 'lucide-react';
import { PRP_NAV, PRP_SIDEBAR_HEADER } from '@/components/layout/nav/prp-nav';
import {
  BrandWordmark,
  NotificationBell,
  SIDEBAR_GRADIENT,
  UserMenu,
} from '@/components/layout/shell';
import type { ShellNavItem, ShellNavSection } from '@/components/layout/shell';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ThemePicker } from '@/components/shared/theme-picker';
import { usePermissions } from '@/hooks/use-permissions';
import { cn } from '@/lib/utils';
import { NewContactNoteDialog } from './new-document-actions';

export function PrpMobileTopBar({ title, onMenu }: { title: string; onMenu: () => void }) {
  return (
    <header className="relative z-40 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card/95 px-3 backdrop-blur-xl">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open menu"
        className="flex h-11 w-11 items-center justify-center rounded-xl text-foreground hover:bg-muted"
      >
        <Menu className="h-5 w-5" />
      </button>
      <p className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">{title}</p>
      <NotificationBell />
      <UserMenu />
    </header>
  );
}

export function PrpMobileTabBar({
  isActive,
  onMenu,
}: {
  isActive: (href: string) => boolean;
  onMenu: () => void;
}) {
  const { can } = usePermissions();
  const [creatingNote, setCreatingNote] = useState(false);
  const tab = (href: string, label: string, Icon: typeof Home) => {
    const active = isActive(href);
    return (
      <Link
        to={href}
        className={cn(
          'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium',
          active ? 'text-primary' : 'text-muted-foreground',
        )}
        aria-current={active ? 'page' : undefined}
      >
        <Icon className="h-5 w-5" />
        {label}
      </Link>
    );
  };
  return (
    <>
      <nav
        aria-label="PRP"
        className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl"
      >
        {tab('/prp', 'Home', Home)}
        {tab('/prp/caseload', 'Caseload', Users)}
        {can('prp', 'create') ? (
          <button
            type="button"
            onClick={() => setCreatingNote(true)}
            className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-primary"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
              <Plus className="h-5 w-5" />
            </span>
            New note
          </button>
        ) : null}
        {tab('/prp/tasks', 'Tasks', ListChecks)}
        <button
          type="button"
          onClick={onMenu}
          className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-muted-foreground"
        >
          <Menu className="h-5 w-5" />
          More
        </button>
      </nav>
      {creatingNote && <NewContactNoteDialog onClose={() => setCreatingNote(false)} />}
    </>
  );
}

export function PrpMobileMenu({
  open,
  onOpenChange,
  isItemActive,
  backToApp,
  sections = PRP_NAV,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isItemActive: (pathname: string, item: ShellNavItem) => boolean;
  backToApp: boolean;
  sections?: ShellNavSection[];
}) {
  const location = useLocation();
  const navigate = useNavigate();
  // Close after navigating.
  useEffect(() => {
    onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[82%] max-w-xs gap-0 overflow-y-auto p-0 text-white"
        style={{ background: SIDEBAR_GRADIENT }}
      >
        <div className="border-b border-white/10 px-4 py-4">
          <BrandWordmark collapsed={false} tone="dark" subtitle={PRP_SIDEBAR_HEADER.subtitle} />
        </div>
        {backToApp && (
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mx-2 mt-2 flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-sm font-semibold text-white/70 hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" /> Back to App
          </button>
        )}
        <nav className="flex-1 py-2">
          {sections.map((section, si) => (
            <div key={section.label} className={si > 0 ? 'mt-1 border-t border-white/10 pt-1' : ''}>
              <p className="px-5 pb-1 pt-3 text-[10px] font-black uppercase tracking-[0.25em] text-white/40">
                {section.label}
              </p>
              {section.items.map((item) => {
                const active = isItemActive(location.pathname, item);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'mx-2 flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-[15px] font-semibold',
                      active ? 'bg-white/20 text-white' : 'text-white/75 hover:bg-white/10',
                    )}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="flex items-center justify-between border-t border-white/10 px-4 py-3 text-sm text-white/70">
          Theme
          <ThemePicker variant="icon" />
        </div>
      </SheetContent>
    </Sheet>
  );
}
